<template>
  <div class="affiliated-company-input-container">
    <FormLabel :label="label" :isRequired="isRequired && !readOnly" />
    <div class="affiliated-company-input-wrapper">
      <!-- readonlyモードではテキスト表示 -->
      <div v-if="readOnly" class="readonly-rows">
        <div v-if="displayItem" class="readonly-row">
          {{ displayItem }}
        </div>
        <div v-else class="readonly-text">-</div>
      </div>
      <!-- 通常モードでは複数行の入力 -->
      <div v-else class="input-rows" :class="{ 'has-error': errorMessage }">
        <div v-for="(item, index) in localItems" :key="index" class="input-row">
          <div class="row-fields">
            <!-- 開始日 -->
            <div class="field field-date">
              <DateInput
                label=""
                :value="item.start_date"
                placeholder="---- / -- / --"
                :disabled="disabled"
                @input="onStartDateChange(index, $event)"
              />
            </div>
            <div class="field field-date-separator">〜</div>
            <!-- 社員区分 -->
            <div class="field field-status">
              <treeselect
                :value="normalizedStatus(item.status)"
                :options="employmentStatusOptionsForTree"
                :disabled="disabled"
                :multiple="false"
                :clearable="false"
                placeholder="社員区分"
                noOptionsText="なし"
                @input="onEmploymentStatusChange(index, $event)"
              />
            </div>
            <!-- 所属企業 -->
            <div class="field field-company">
              <TextInput
                label=""
                :value="item.name"
                placeholder="所属企業"
                :disabled="disabled || isPermanentEmployee(index)"
                @input="onAffiliatedCompanyChange(index, $event)"
              />
            </div>
            <!-- 削除ボタン -->
            <div class="field field-delete" v-if="localItems.length > 1">
              <div
                class="flex justify-center items-center action-button-icon p-1"
                @click.stop="removeRow(index)"
              >
                <vs-icon icon="delete_outline" size="22px" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        <!-- 追加ボタン -->
        <div class="add-button-wrapper flex justify-center">
          <TextButton
            type="border"
            colorType="secondary"
            label="+ 社員区分 / 所属企業を追加"
            :disabled="disabled"
            @click="addRow"
          />
        </div>
      </div>
    </div>

    <InputBottomDivider v-if="readOnly" />
  </div>
</template>

<script>
import FormLabel from "./FormLabel.vue";
import InputBottomDivider from "./InputBottomDivider.vue";
import DateInput from "./DateInput.vue";
import TextInput from "./TextInput.vue";
import Button from "../Button.vue";
import TextButton from "../TextButton.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { mapState } from "vuex";
import {
  EmploymentStatusOptionsForTree,
  getEmploymentStatusLabel,
  isPermanentEmployee as checkIsPermanentEmployee,
} from "../../constants/EmploymentStatus.js";

export default {
  name: "AffiliatedCompanyInput",
  components: {
    FormLabel,
    InputBottomDivider,
    DateInput,
    TextInput,
    Button,
    TextButton,
    Treeselect,
  },
  props: {
    /**
     * ラベルテキスト
     * @type {String}
     * @required
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * 入力値（配列）
     * @type {Array}
     * @default () => []
     * @example [{ start_date: "2024-01-01", status: "PERMANENT", name: "株式会社アイディア" }]
     */
    value: {
      type: Array,
      default: () => [],
    },
    /**
     * 必須項目かどうか
     * @type {Boolean}
     * @default false
     */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * 読み取り専用かどうか
     * @type {Boolean}
     * @default false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーメッセージ
     * @type {String}
     * @default ""
     */
    errorMessage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      localItems: [],
    };
  },
  watch: {
    value: {
      handler(newValue) {
        // valueが空の場合は初期行を1つ作成
        if (!newValue || newValue.length === 0) {
          this.localItems = [
            {
              start_date: null,
              status: null,
              name: "",
            },
          ];
        } else {
          // 既存のデータをコピーし、社員区分が「社員」の場合は所属企業名を自社名に設定
          const updatedItems = newValue.map(item => {
            const isPermanent = checkIsPermanentEmployee(item.status);
            return {
              ...item,
              // 社員区分が「社員」で、所属企業名が空の場合は自社名を設定
              name:
                isPermanent && (!item.name || item.name.trim() === "")
                  ? this.companyName
                  : item.name || "",
            };
          });

          // 変更があったかどうかをチェック
          const hasChanges = updatedItems.some((item, index) => {
            const originalItem = newValue[index];
            return item.name !== (originalItem.name || "");
          });

          this.localItems = updatedItems;

          // 変更があった場合のみ親に通知（無限ループを避けるため）
          if (hasChanges) {
            this.$emit("input", updatedItems);
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    companyName() {
      return this.user && this.user.name ? this.user.name : "";
    },
    employmentStatusOptionsForTree() {
      return EmploymentStatusOptionsForTree;
    },
    displayItem() {
      if (!this.value || this.value.length === 0) {
        return null;
      }

      // start_dateが最新のitemを取得
      const itemsWithDate = this.value
        .filter(item => item.start_date)
        .map(item => ({
          ...item,
          dateValue: new Date(item.start_date),
        }))
        .filter(item => !isNaN(item.dateValue.getTime()));

      if (itemsWithDate.length === 0) {
        // start_dateがない場合は最初のitemを使用
        const firstItem = this.value[0];
        if (!firstItem) {
          return null;
        }
        return this.formatItem(firstItem);
      }

      // start_dateが最新のitemを選択
      const latestItem = itemsWithDate.reduce((latest, current) => {
        return current.dateValue > latest.dateValue ? current : latest;
      });

      return this.formatItem(latestItem);
    },
  },
  methods: {
    /**
     * treeselect用にステータス値を正規化
     * 空文字列やundefinedの場合はnullを返し、プレースホルダーを表示させる
     */
    normalizedStatus(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      return value;
    },
    formatItem(item) {
      const datePart = [];
      const otherParts = [];

      if (item.start_date) {
        const date = new Date(item.start_date);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          datePart.push(`${year}/${month}/${day}〜`);
        }
      }
      if (item.status) {
        otherParts.push(getEmploymentStatusLabel(item.status));
      }
      if (item.name) {
        otherParts.push(item.name);
      }

      if (datePart.length === 0 && otherParts.length === 0) {
        return "-";
      }

      const dateStr = datePart.join("");
      const otherStr = otherParts.join(" / ");

      if (dateStr && otherStr) {
        return `${dateStr}　${otherStr}`;
      } else if (dateStr) {
        return dateStr;
      } else {
        return otherStr;
      }
    },
    onStartDateChange(index, dateValue) {
      const newItems = [...this.localItems];
      newItems[index] = {
        ...newItems[index],
        start_date: dateValue,
      };
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    onEmploymentStatusChange(index, selectedStatus) {
      const newItems = [...this.localItems];
      const isPermanent = checkIsPermanentEmployee(selectedStatus);

      newItems[index] = {
        ...newItems[index],
        status: selectedStatus || null,
        // 社員区分が「社員」の場合、所属企業に自社名を自動入力
        name: isPermanent ? this.companyName : newItems[index].name,
      };
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    isPermanentEmployee(index) {
      const item = this.localItems[index];
      return item && checkIsPermanentEmployee(item.status);
    },
    onAffiliatedCompanyChange(index, value) {
      const newItems = [...this.localItems];
      newItems[index] = {
        ...newItems[index],
        name: value,
      };
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    addRow() {
      const newItems = [
        ...this.localItems,
        {
          start_date: null,
          status: null,
          name: "",
        },
      ];
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    removeRow(index) {
      // アイテムが1個の場合は削除できない
      if (this.localItems.length <= 1) {
        return;
      }
      const newItems = this.localItems.filter((_, i) => i !== index);
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.affiliated-company-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .affiliated-company-input-wrapper {
    .readonly-rows {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .readonly-row {
        padding: 0rem 0.75rem;
        font-family: $aisea-font-family;
        font-size: 16px;
        font-weight: 400;
        color: $aisea-dark-gray;
        line-height: 1.5;
        min-height: 26px;
      }

      .readonly-text {
        padding: 0rem 0.75rem;
        font-family: $aisea-font-family;
        font-size: 16px;
        font-weight: 400;
        color: $aisea-dark-gray;
        line-height: 1.5;
        min-height: 26px;
      }
    }

    .input-rows {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .input-row {
        .row-fields {
          display: flex;
          gap: 0.5rem;
          align-items: center;

          .field {
            display: flex;
            flex-direction: column;

            &.field-date {
              width: 140px;
              flex-shrink: 0;
            }

            &.field-status {
              width: 150px;
              flex-shrink: 0;
            }

            &.field-company {
              flex: 1;
            }

            .action-button-icon {
              border-radius: 5px;
              cursor: pointer;
              transition: all 0.3s ease;
              &:hover {
                background: rgb(199, 199, 199);
              }
            }

            ::v-deep .vue-treeselect {
              .vue-treeselect__control {
                border: 1px solid $aisea-mouse-gray;
                border-radius: 6px;
                min-height: 36px;
                transition: border-color 0.3s ease, box-shadow 0.3s ease;
              }

              .vue-treeselect__control--focused {
                border-color: #2d9cdb;
                box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
              }

              &.vue-treeselect--disabled {
                .vue-treeselect__control {
                  background-color: #f5f5f5;
                  border-color: $aisea-light-gray;
                  cursor: not-allowed;
                }
              }
            }
          }
        }
      }

      &.has-error {
        .input-row {
          .row-fields {
            .field {
              ::v-deep .vue-treeselect {
                .vue-treeselect__control {
                  border-color: $aisea-red;
                  background-color: rgba($aisea-red, 0.1);
                }
              }

              ::v-deep .date-input-container {
                .flat-pickr-wrapper {
                  input {
                    border-color: $aisea-red !important;
                    background-color: rgba($aisea-red, 0.1) !important;
                  }
                }
              }

              ::v-deep .text-input-container {
                .text-input {
                  border-color: $aisea-red;
                  background-color: rgba($aisea-red, 0.1);
                }
              }
            }
          }
        }
      }
    }
  }

  .error-message {
    margin-top: 0.25rem;
    padding-left: 0.25rem;
    font-family: $aisea-font-family;
    font-size: 12px;
    font-weight: 400;
    color: $aisea-red;
    line-height: 1.5;
  }
}
</style>
